/* eslint-disable no-unused-expressions */
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Offcanvas, OverlayTrigger } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import {
  resetEverything,
  resetIsImageProcessingBasedOnId,
  resetIsShowProcessedImageBasedOnId,
  setIsImageProcessing,
  setIsShowProcessedImageAfterRemoval,
  setRemoveBackgroundImageData,
  setShouldCallFunction,
  setSingleImageObj,
} from '../../app/services/removeBackground';
import { ShopStyled } from '../shopSetting/assets/shopStyled';
import useAuth from '../../hooks/useAuth';
import {
  formatTimestamp,
  getButtonHover,
  getColor,
  getDarkenColor,
  removeFileNameDots,
} from '../../utils/helper';
import { removeBackground } from '@imgly/background-removal';
import { useUploadImageToS3BucketMutation } from './imagesGalleryApiSlice';
import { setGalleryImages } from '../../app/services/imagesGallery';
import StyledLoading from '../../app/components/loading/StyledLoading';
import DeleteConfirmation from '../../app/components/popups/DeleteConfirmation';
import html2canvas from 'html2canvas';
import DropDownIcon from '../../app/components/svgIcons/DropDownIcon';
import ZoomOutIcon from '../../app/components/svgIcons/ZoomOutIcon';
import ZoomInIcon from '../../app/components/svgIcons/ZoomInIcon';
import { StyledTooltip } from '../../app/components/styledComponents/StyledTooltip';
import UndoIcon from '../../app/components/svgIcons/UndoIcon';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Dropdown from 'react-bootstrap/Dropdown';

/* Images */
const images = [
  `noImage.jpg`,
  `product_background_0.jpg`,
  `product_background_1.jpg`,
  `product_background_2.jpg`,
  `product_background_3.jpg`,
  `product_background_4.jpg`,
  `product_background_5.jpg`,
  `product_background_6.jpg`,
  `product_background_7.jpg`,
  `product_background_8.jpg`,
  `product_background_9.jpg`,
  `product_background_10.jpg`,
  `product_background_11.JPG`,
  `product_background_12.JPG`,
];

const StyledContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const BootstrapEditButton = styled(Button)`
  font-family: ${({ theme }) => theme.fontFamily};
  padding: ${({ padding }) => padding || '9px 16px'} !important;
  font-size: 18px;
  box-shadow: ${({ noBoxShadow }) =>
    !noBoxShadow
      ? `0 0 4px 0 rgba(0, 0, 0, 0.11),
    0 8px 16px 0 ${({ theme, color }) => `${color ?? theme.primary}3D`}`
      : 'none'};

  border: 1.5px solid
    ${({ color, theme, variant, active }) =>
      active
        ? theme.dark30
        : variant === 'outline' && color === 'heading'
        ? theme.dark70
        : getColor(color, theme)};

  background-color: ${({ color, theme, variant }) =>
    variant === 'outline' && color === 'heading'
      ? theme.transparent
      : getColor(color, theme)};

  color: ${({ color, theme, variant }) =>
    variant === 'outline' && color === 'heading'
      ? theme.heading
      : variant !== 'outline'
      ? getButtonHover(color, theme)
      : theme.white};

  ${(props) =>
    props.type === 'error' &&
    css`
      background-color: ${({ theme }) => theme.white};
      box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.11),
        0 8px 16px 0 rgba(0, 0, 0, 0.12);
      color: ${({ theme }) => theme.error};
      &:hover {
        background-color: ${({ theme }) => theme.white}!important;
        color: ${({ theme }) => getButtonHover('error', theme)}!important;
      }
    `}

  &:hover {
    background-color: ${({ color, theme, variant }) =>
      variant === 'outline' && color === 'heading'
        ? theme.dark70
        : getDarkenColor(color, theme)};
    color: ${({ color, theme, variant }) =>
      variant === 'outline' && color === 'heading'
        ? theme.heading
        : variant !== 'outline'
        ? getButtonHover(color, theme)
        : theme.white};
    border: 1.5px solid
      ${({ color, theme, variant }) =>
        variant === 'outline' && color === 'heading'
          ? theme.dark70
          : getDarkenColor(color, theme)};
  }

  &:active,
  :visited {
    background-color: ${({ color, theme, variant }) =>
      variant === 'outline' && color === 'heading'
        ? theme.dark70
        : getDarkenColor(color, theme)} !important;
    color: ${({ color, theme, variant }) =>
      variant === 'outline' && color === 'heading'
        ? theme.heading
        : variant !== 'outline'
        ? getButtonHover(color, theme)
        : theme.white} !important;
    border: 1.5px solid
      ${({ color, theme, variant }) =>
        variant === 'outline' && color === 'heading'
          ? theme.dark70
          : getDarkenColor(color, theme)} !important;
  }

  ${(props) =>
    (props.type === 'icon-btn' || props.variant === 'icon-btn') &&
    css`
      background-color: ${({ theme }) => theme.transparent};
      box-shadow: none !important;
      color: ${({ theme }) => theme.heading};
      &:hover {
        background-color: ${({ theme }) => theme.transparent}!important;
        color: ${({ theme }) => getButtonHover('heading', theme)}!important;
      }
    `}

  ${(props) =>
    (props.type === 'icon-btn-text' || props.variant === 'icon-btn-text') &&
    css`
      background-color: ${({ theme }) => theme.transparent};
      box-shadow: none !important;
      color: ${({ theme }) => theme.heading};
      font-weight: 600;
      border: 2px solid ${({ theme }) => theme.primary};
      &:hover {
        background-color: ${({ theme }) => theme.transparent}!important;
        color: ${({ theme }) => getButtonHover('heading', theme)}!important;
      }
    `}
`;

const BootstrapDropDownButton = styled(Dropdown.Toggle)`
  font-family: ${({ theme }) => theme.fontFamily};
  padding: ${({ padding }) => padding || '9px 16px'} !important;
  box-shadow: ${({ noBoxShadow }) =>
    !noBoxShadow
      ? `0 0 4px 0 rgba(0, 0, 0, 0.11),
    0 8px 16px 0 ${({ theme, color }) => `${color ?? theme.primary}3D`}`
      : 'none'};

  border: 1.5px solid
    ${({ color, theme, variant, active }) =>
      active
        ? theme.dark30
        : variant === 'outline' && color === 'heading'
        ? theme.dark70
        : getColor(color, theme)};

  background-color: ${({ color, theme, variant }) =>
    variant === 'outline' && color === 'heading'
      ? theme.transparent
      : getColor(color, theme)};

  color: ${({ color, theme, variant }) =>
    variant === 'outline' && color === 'heading'
      ? theme.heading
      : variant !== 'outline'
      ? getButtonHover(color, theme)
      : theme.white};

  ${(props) =>
    props.type === 'error' &&
    css`
      background-color: ${({ theme }) => theme.white};
      box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.11),
        0 8px 16px 0 rgba(0, 0, 0, 0.12);
      color: ${({ theme }) => theme.error};
      &:hover {
        background-color: ${({ theme }) => theme.white}!important;
        color: ${({ theme }) => getButtonHover('error', theme)}!important;
      }
    `}

  &.show {
    background-color: ${({ color, theme, variant }) =>
      variant === 'outline' && color === 'heading'
        ? theme.dark70
        : getDarkenColor(color, theme)};
    color: ${({ color, theme, variant }) =>
      variant === 'outline' && color === 'heading'
        ? theme.heading
        : variant !== 'outline'
        ? getButtonHover(color, theme)
        : theme.white};
    border: 1.5px solid
      ${({ color, theme, variant }) =>
        variant === 'outline' && color === 'heading'
          ? theme.dark70
          : getDarkenColor(color, theme)};
  }
  &:hover {
    background-color: ${({ color, theme, variant }) =>
      variant === 'outline' && color === 'heading'
        ? theme.dark70
        : getDarkenColor(color, theme)};
    color: ${({ color, theme, variant }) =>
      variant === 'outline' && color === 'heading'
        ? theme.heading
        : variant !== 'outline'
        ? getButtonHover(color, theme)
        : theme.white};
    border: 1.5px solid
      ${({ color, theme, variant }) =>
        variant === 'outline' && color === 'heading'
          ? theme.dark70
          : getDarkenColor(color, theme)};
  }

  &:active,
  :visited {
    background-color: ${({ color, theme, variant }) =>
      variant === 'outline' && color === 'heading'
        ? theme.dark70
        : getDarkenColor(color, theme)} !important;
    color: ${({ color, theme, variant }) =>
      variant === 'outline' && color === 'heading'
        ? theme.heading
        : variant !== 'outline'
        ? getButtonHover(color, theme)
        : theme.white} !important;
    border: 1.5px solid
      ${({ color, theme, variant }) =>
        variant === 'outline' && color === 'heading'
          ? theme.dark70
          : getDarkenColor(color, theme)} !important;
  }

  ${(props) =>
    (props.type === 'icon-btn' || props.variant === 'icon-btn') &&
    css`
      background-color: ${({ theme }) => theme.transparent};
      box-shadow: none !important;
      color: ${({ theme }) => theme.heading};
      &:hover {
        background-color: ${({ theme }) => theme.transparent}!important;
        color: ${({ theme }) => getButtonHover('heading', theme)}!important;
      }
    `}

  ${(props) =>
    (props.type === 'icon-btn-text' || props.variant === 'icon-btn-text') &&
    css`
      background-color: ${({ theme }) => theme.transparent};
      box-shadow: none !important;
      color: ${({ theme }) => theme.heading};
      font-weight: 600;
      border: 2px solid ${({ theme }) => theme.primary};
      &:hover {
        background-color: ${({ theme }) => theme.transparent}!important;
        color: ${({ theme }) => getButtonHover('heading', theme)}!important;
      }
    `}
`;

const BootstrapDropDownItem = styled(Dropdown.Item)`
  &:hover {
    background-color: ${({ color, theme, variant }) =>
      variant === 'outline' && color === 'heading'
        ? theme.dark70
        : getDarkenColor(color, theme)};
    color: ${({ color, theme, variant }) =>
      variant === 'outline' && color === 'heading'
        ? theme.heading
        : variant !== 'outline'
        ? getButtonHover(color, theme)
        : theme.white};
  }

  &:active,
  :visited {
    background-color: ${({ color, theme, variant }) =>
      variant === 'outline' && color === 'heading'
        ? theme.dark70
        : getDarkenColor(color, theme)} !important;
    color: ${({ color, theme, variant }) =>
      variant === 'outline' && color === 'heading'
        ? theme.heading
        : variant !== 'outline'
        ? getButtonHover(color, theme)
        : theme.white} !important;
  }
`;
const StylesImageWrapper = styled.div`
  &.transparent-bg {
    background: url(http://www.zingerbugimages.com/backgrounds/gray_and_white_checkers.gif);
    background-position: center left !important;
    background-repeat: repeat !important;
  }
  &.custom-bg {
    background-position-x: ${({ backgroundPositionX }) =>
      backgroundPositionX || backgroundPositionX === 0
        ? backgroundPositionX + '%'
        : 'auto'} !important;
    background-position-y: ${({ backgroundPositioY }) =>
      backgroundPositioY || backgroundPositioY === 0
        ? backgroundPositioY + '%'
        : 'auto'} !important;
    background-size: ${({ backgroundSize }) =>
      backgroundSize ? backgroundSize + '%' : 'auto'} !important;
    cursor: ${({ isDragging }) =>
      isDragging ? 'grabbing' : 'default'} !important;
    background-repeat: no-repeat !important;

    > img {
      pointer-events: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
    }
  }
  &.image-container-remove-background-body {
    background-size: cover !important;
    background-position: center !important;
    background-repeat: no-repeat !important;
  }
`;

const StylesDivWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2px;
  cursor: pointer;
  padding: 5px;
  &:hover {
    background-color: rgb(236, 243, 255);
  }
`;

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

const messages = [
  'Analyzing the image...',
  'Removing background...',
  'Optimizing image...',
  'Finalizing process...',
];
const RemoveBackgroundImage = () => {
  const dispatch = useDispatch();
  const RemoveBackgroundImageData = useSelector(
    (state) => state.removeBackground,
  );
  const { user } = useAuth();
  const [textForImageProcessing, setTextForImageProcessing] = useState(
    messages[0],
  );
  const [selectedImage, setSelectedImage] = useState(null);
  const [isProcessingStart, setIsProcessingStart] = useState(false);
  const [uploadToBucket] = useUploadImageToS3BucketMutation();

  const uploadedOn = formatTimestamp(Date.now().toString());
  useEffect(() => {
    if (isProcessingStart) {
      let messageIndex = 0;
      setTextForImageProcessing(messages[messageIndex]); // Set initial message

      const interval = setInterval(() => {
        messageIndex++;
        if (messageIndex < messages.length) {
          setTextForImageProcessing(messages[messageIndex]);
        } else {
          clearInterval(interval);
        }
      }, 4000);

      return () => clearInterval(interval);
    }
  }, [isProcessingStart]);

  const handleBackgroundRemoval = async (selectedImage, model = 'medium') => {
    const singleImageObj = {
      progress: 0,
      uploading: true,
      uploadedOn: uploadedOn,
      uploadedBy: `${user.firstName} ${user.lastName}`,
      uploadedTo: '',
      fileSize: '',
      dimension: '',
      alternativeText: selectedImage?.alternativeText || '',
      title: selectedImage?.title || '',
      caption: selectedImage?.caption || '',
      description: selectedImage?.description || '',
      copyLink: selectedImage?.copyLink || '',
      tagDTOS: selectedImage?.tagDTOS || [],
      imageDTO: {
        content: '',
        fileName: '',
        extension: '',
      },
    };

    if (
      !selectedImage &&
      !selectedImage?.imageDTO &&
      !selectedImage?.imageDTO?.url
    )
      return;

    dispatch(setIsImageProcessing(selectedImage.id));
    //    setisImageProcessing([...isImageProcessing, selectedImage.id]);
    const imageUrl = selectedImage?.imageDTO?.url || selectedImage;

    try {
      // Fetch the image as a Blob
      const response = await fetch(imageUrl);

      let imageBlob = await response.blob();
      setIsProcessingStart(true);

      // Check the image type and convert to PNG if needed
      if (!['image/jpeg', 'image/webp', 'image/png'].includes(imageBlob.type)) {
        const canvas = document.createElement('canvas');
        const img = new Image();
        img.src = URL.createObjectURL(imageBlob);

        await new Promise((resolve, reject) => {
          img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            canvas.toBlob((blob) => {
              if (blob) {
                imageBlob = blob;
                resolve();
              } else {
                reject(new Error('Failed to convert image to PNG'));
              }
            }, 'image/png');
          };
          img.onerror = reject;
        });
      }

      const config = {
        model: model === 'High' ? 'medium' : 'small',
      };

      // Remove the background
      const resultBlob = await removeBackground(imageBlob, config);

      // Get file type
      const fileType = resultBlob.type;

      const img = new Image();
      img.src = URL.createObjectURL(resultBlob);
      img.onload = async () => {
        const dimension = `${img.width} by ${img.height} Pixels`;

        // Create a URL for the result
        const base64String = await blobToBase64(resultBlob);
        // Create a new File object
        const newFile = new File(
          [resultBlob],
          selectedImage?.imageDTO?.fileName || 'edited_image',
          {
            type: fileType,
          },
        );
        singleImageObj.dimension = dimension;
        singleImageObj.imageDTO = {
          fileName: removeFileNameDots(newFile?.name),
          extension: fileType,
          url: img.src,
        };
        singleImageObj.originalFile = newFile;
        setIsProcessingStart(false);
        if (base64String) {
          // Create a copy of the selected image with processed image URL and processed flag
          const updatedSelectedImage = { ...selectedImage };
          updatedSelectedImage.processedImageUrl = base64String;
          updatedSelectedImage.processed = true;

          // Dispatch an action to set the updated selected image data
          dispatch(setRemoveBackgroundImageData(updatedSelectedImage));

          // Check if the image is not already in the showCanvas array and dispatch an action accordingly
          dispatch(
            setIsShowProcessedImageAfterRemoval(updatedSelectedImage?.id),
          );
          dispatch(setSingleImageObj(singleImageObj));

          //   handleImageEditOrSave(null, 'initial');
        }
      };
    } catch (error) {
      console.error('Remove background error: ', error);
      setIsProcessingStart(false);
    } finally {
      dispatch(resetIsImageProcessingBasedOnId(selectedImage.id));
      setIsProcessingStart(false);
    }
  };

  const handleSave = async () => {
    const singleImageObj = { ...RemoveBackgroundImageData?.singleImageObj };
    singleImageObj.uploadedOn = uploadedOn;

    const payload = {
      floristId: user?.floristId,
      isFlorist: true,
      shopName: user?.floristDTO?.companyName,
      uuid: user?.floristDTO?.uuid,
      floristImagesDTOS: [singleImageObj],
    };
    dispatch(resetEverything());
    dispatch(setGalleryImages({ images: [singleImageObj], isFlorist: true }));
    await uploadToBucket(payload);
    dispatch(setShouldCallFunction(true));
  };
  return (
    <>
      {RemoveBackgroundImageData &&
        RemoveBackgroundImageData?.removeBackgroundImageData.length > 0 &&
        RemoveBackgroundImageData?.removeBackgroundImageData.map(
          (item, index) => (
            <Offcanvas
              show={RemoveBackgroundImageData?.showCanvas.length > 0 || false}
              placement={'end'}
              key={index}
              style={{ width: '80%', zIndex: 1050 }}
            >
              <Offcanvas.Body
                style={{
                  padding: '0px',
                }}
              >
                <div style={{ display: 'flex', width: '100%', height: '100%' }}>
                  <div
                    className="d-flex gap-2"
                    style={{
                      flexDirection: 'column',
                      background: 'white',
                      padding: '25px',
                      overflow: 'auto',
                      boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    {images.map((image, index) => (
                      <img
                        key={index}
                        alt={`background${index + 1}`}
                        src={`https://thumbnails.boostfloral.com/background-images/${image}`}
                        height={47}
                        width={47}
                        style={{
                          cursor: 'pointer',
                          transition: 'transform 0.3s ease', // Add transition for smooth animation
                          borderRadius: '3px',
                          border: index === 0 ? '1px solid #BEBEBE' : '0px',
                        }}
                        onClick={() =>
                          index === 0
                            ? setSelectedImage(null)
                            : setSelectedImage(
                                `https://images.boostfloral.com/background-images/${image}`,
                              )
                        }
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.transform = 'scale(1.2)')
                        } // Increase size on hover
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.transform = 'scale(1)')
                        } // Reset size when hover ends
                      />
                    ))}
                  </div>

                  <ProcessedImage
                    key={index}
                    image={item}
                    RemoveBackgroundImageData={RemoveBackgroundImageData}
                    dispatch={dispatch}
                    handleBackgroundRemoval={handleBackgroundRemoval}
                    handleSave={handleSave}
                    user={user}
                    setSelectedImage={setSelectedImage}
                    selectedImage={selectedImage}
                    uploadToBucket={uploadToBucket}
                    textForImageProcessing={textForImageProcessing}
                  />
                </div>
              </Offcanvas.Body>
            </Offcanvas>
          ),
        )}
    </>
  );
};

export default RemoveBackgroundImage;

const ProcessedImage = ({
  image,
  dispatch,
  handleBackgroundRemoval,
  RemoveBackgroundImageData,
  handleSave,
  uploadToBucket,
  user,
  textForImageProcessing,
  setSelectedImage,
  selectedImage,
}) => {
  const [isBackgroundImageShow, setIsBackgroundImageShow] = useState(false);
  const imageRef = useRef(null);
  const MAX_SIZE_MB = 8;
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

  const [zoomLevel, setZoomLevel] = useState(100);
  const [positionX, setPositionX] = useState(50); // 50% means centered
  const [positionY, setPositionY] = useState(50); // 50% means centered vertically
  const [isDragging, setIsDragging] = useState(false);
  const [model, setModel] = useState('High');
  const [isReset, setIsReset] = useState(false);
  const [lastClientX, setLastClientX] = useState(0);
  const [lastClientY, setLastClientY] = useState(0);

  const isProcessing = RemoveBackgroundImageData?.isImageProcessing?.includes(
    image.id,
  );

  const updateValue = (currentValue, change, min, max) =>
    Math.min(Math.max(currentValue + change, min), max);

  const handleAction = (event, type) => {
    event.stopPropagation();
    event.preventDefault();
    switch (type) {
      case 'zoomIn':
        setZoomLevel((prevZoom) => updateValue(prevZoom, 10, 10, 500));
        break;
      case 'zoomOut':
        setZoomLevel((prevZoom) => updateValue(prevZoom, -10, 10, 500));
        break;
      case 'moveLeft':
        setPositionX((prevPosition) =>
          updateValue(prevPosition, -10, -1000, 1000),
        );
        break;
      case 'moveRight':
        setPositionX((prevPosition) =>
          updateValue(prevPosition, 10, -1000, 1000),
        );
        break;
      case 'moveUp':
        setPositionY((prevPosition) =>
          updateValue(prevPosition, -10, -1000, 1000),
        );
        break;
      case 'moveDown':
        setPositionY((prevPosition) =>
          updateValue(prevPosition, 10, -1000, 1000),
        );
        break;
      case 'reset':
        setZoomLevel(100);
        setPositionX(50);
        setPositionY(50);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const container = imageRef.current;

    const handleWheel = (event) => {
      event.preventDefault();
      // handleAction function definition should be provided
      handleAction(event, event.deltaY < 0 ? 'zoomIn' : 'zoomOut');
    };

    const handlePointerDown = (event) => {
      setIsDragging(true);
      setLastClientX(event.clientX);
      setLastClientY(event.clientY);
    };

    const handlePointerMove = (event) => {
      if (isDragging) {
        const deltaX = event.clientX - lastClientX;
        const deltaY = event.clientY - lastClientY;
        setPositionX((prevPosition) =>
          Math.min(Math.max(prevPosition + deltaX / 5, 0), 100),
        );
        setPositionY((prevPosition) =>
          Math.min(Math.max(prevPosition + deltaY / 5, 0), 100),
        );
        setLastClientX(event.clientX);
        setLastClientY(event.clientY);
      }
    };

    const handlePointerUp = () => {
      setIsDragging(false);
    };

    if (selectedImage) {
      container.addEventListener('wheel', handleWheel);
      container.addEventListener('pointerdown', handlePointerDown);
      container.addEventListener('pointermove', handlePointerMove);
      container.addEventListener('pointerup', handlePointerUp);
      container.addEventListener('pointerleave', handlePointerUp);
    }

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerup', handlePointerUp);
      container.removeEventListener('pointerleave', handlePointerUp);
    };
  }, [selectedImage, isDragging, lastClientX, lastClientY]);

  // Add keypress event listeners
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (selectedImage && !isProcessing) {
        if (event?.key === 'ArrowUp') {
          handleAction(event, 'moveUp');
        }
        if (event?.key === 'ArrowDown') {
          handleAction(event, 'moveDown');
        }
        if (event?.key === 'ArrowRight') {
          handleAction(event, 'moveRight');
        }
        if (event?.key === 'ArrowLeft') {
          handleAction(event, 'moveLeft');
        }
        if (event?.key === '+' && event.ctrlKey) {
          handleAction(event, 'zoomIn');
        }
        if (event?.key === '-' && event.ctrlKey) {
          handleAction(event, 'zoomOut');
        }
        if (event?.key === '0' && event.ctrlKey) {
          setIsReset(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleAction, selectedImage, image, isProcessing]);

  const handleSaveImage = async () => {
    try {
      dispatch(setIsImageProcessing(image.id));
      if (imageRef.current) {
        setTimeout(() => {
          html2canvas(imageRef.current, {
            useCORS: true,
          }).then((canvas) => {
            const processBlob = async (quality) => {
              canvas.toBlob(
                async (blob) => {
                  if (!blob) {
                    await dispatch(resetIsImageProcessingBasedOnId(image.id));
                    return;
                  }

                  // If the blob size is greater than the max size, reduce quality and retry
                  if (blob.size > MAX_SIZE_BYTES && quality > 0.1) {
                    return processBlob(quality - 0.1);
                  }

                  const img = new Image();
                  img.src = URL.createObjectURL(blob);
                  img.onload = async () => {
                    const dimension = `${img.width} by ${img.height} Pixels`;
                    const fileType = 'image/jpeg';

                    const base64String = await blobToBase64(blob);

                    const newFile = new File(
                      [blob],
                      image?.imageDTO?.fileName || 'edited_image.jpeg',
                      {
                        type: fileType,
                      },
                    );

                    const singleImageObjs = {
                      progress: 0,
                      uploading: true,
                      uploadedOn: formatTimestamp(Date.now().toString()),
                      uploadedBy: `${user.firstName} ${user.lastName}`,
                      uploadedTo: '',
                      fileSize: '',
                      dimension,
                      alternativeText: image?.alternativeText || '',
                      title: image?.title || '',
                      caption: image?.caption || '',
                      description: image?.description || '',
                      copyLink: image?.copyLink || '',
                      tagDTOS: image?.tagDTOS || [],
                      imageDTO: {
                        fileName: removeFileNameDots(newFile.name),
                        extension: fileType,
                        url: img.src,
                      },
                      originalFile: newFile,
                    };

                    if (base64String) {
                      const updatedSelectedImage = { ...image };
                      updatedSelectedImage.processedImageUrl = base64String;

                      // Dispatch an action to set the updated selected image data
                      await dispatch(
                        setRemoveBackgroundImageData(updatedSelectedImage),
                      );
                      await dispatch(setSingleImageObj(singleImageObjs));
                      await dispatch(resetIsImageProcessingBasedOnId(image.id));

                      const payload = {
                        floristId: user?.floristId,
                        isFlorist: true,
                        shopName: user?.floristDTO?.companyName,
                        uuid: user?.floristDTO?.uuid,
                        floristImagesDTOS: [singleImageObjs],
                      };

                      dispatch(resetEverything());
                      dispatch(
                        setGalleryImages({
                          images: [singleImageObjs],
                          isFlorist: true,
                        }),
                      );
                      await uploadToBucket(payload);
                      dispatch(setShouldCallFunction(true));
                    }

                    // Clean up the object URL
                    URL.revokeObjectURL(img.src);
                  };
                },
                'image/jpeg',
                quality,
              );
            };

            // Start the blob processing with the highest quality
            processBlob(1.0);
            setSelectedImage(null);
          });
        }, 10);
      }
    } catch (e) {
      setSelectedImage(null);
      console.log(e);
    }
  };

  let isRemovebackground = false;

  if (!image?.processed && !selectedImage) {
    isRemovebackground = true;
  } else {
    isRemovebackground = false;
  }

  return (
    <StyledContainer
      style={{
        width: '100%',
        paddingTop: '16px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: '10px',
          justifyContent: 'flex-start',
          paddingRight: '16px',
          gap: '5px',
          marginBottom: '10px',
        }}
      >
        <span
          style={{
            fontSize: '18px',
          }}
        >
          Edit Background
        </span>
      </div>
      <div
        className="image-container-remove-background"
        style={{
          overflowX: 'auto',
          display: 'flex',
          justifyContent: 'center',
          paddingLeft: '10px',
          paddingRight: '16px',
          maxHeight: 'calc(100vh - 140px)',
          width: '100%',
          height: '100% !important',
          position: 'relative',
        }}
      >
        <div
          ref={imageRef}
          style={{
            maxHeight: 'calc(100vh - 140px)',
            position: 'relative',
            filter: isProcessing && selectedImage && 'brightness(0.5)',
            cursor: isProcessing && selectedImage ? 'progress' : 'default',
            overflow: 'hidden',
          }}
          // className={`${
          //   selectedImage || image?.processed
          //     ? 'transparent-bg'
          //     : 'image-container-remove-background-body'
          // } ${!isProcessing && selectedImage ? 'custom-bg' : ''}`}
          // backgroundSize={zoomLevel}
          // backgroundPositionX={positionX}
          // backgroundPositioY={positionY}
          // isDragging={isDragging}
        >
          <StylesImageWrapper
            className="custom-bg"
            isDragging={isDragging}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 0,
              width: '100%',
              height: '100%',
            }}
          >
            {selectedImage ? (
              <img
                src={selectedImage}
                alt="background"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  pointerEvents: 'none',
                  transform: `scale(${zoomLevel / 100}) translate(${
                    positionX - 50
                  }%, ${positionY - 50}%)`,
                  transformOrigin: 'center center',
                  transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                }}
                crossOrigin="anonymous"
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  pointerEvents: 'none',
                  background : `url('http://www.zingerbugimages.com/backgrounds/gray_and_white_checkers.gif')`
                }}
              ></div>
            )}
          </StylesImageWrapper>

          <img
            src={image?.processedImageUrl}
            alt="processedImage"
            style={{
              maxHeight: 'calc(100vh - 140px)',
              filter: isProcessing && !selectedImage && 'brightness(0.5)',
              cursor: isProcessing ? 'progress' : 'default',
              position: 'relative',
              zIndex: 1,
            }}
          />
        </div>
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: isProcessing ? 'block' : 'none',
            zIndex: 10,
          }}
        >
          <StyledLoading
            style={{ height: '4rem', width: '4rem' }}
            minHeight="5rem"
          />
          <span
            style={{
              color: 'white',
              fontSize: '15px',
            }}
          >
            {' '}
            {textForImageProcessing}
          </span>
        </div>
      </div>
      <div
        className="d-flex justify-content-between w-100 align-items-center"
        style={{
          marginTop: '10px !important',
          background: 'white',
          padding: '13.5px',
          width: '100%',
          boxShadow: 'rgba(0, 0, 0, 0.2) 8px 2px 8px 0px',
        }}
      >
        <div></div>
        {selectedImage && !isProcessing && (
          <div
            style={{
              zIndex: 1,
              gap: '3px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {' '}
            <OverlayTrigger
              overlay={
                <StyledTooltip>
                  {
                    <>
                      <span
                        style={{
                          fontSize: '12px',
                        }}
                      >
                        Ctrl
                      </span>{' '}
                      <span>+</span>{' '}
                      <span
                        style={{
                          fontSize: '12px',
                        }}
                      >
                        Plus
                      </span>
                    </>
                  }
                </StyledTooltip>
              }
            >
              <StylesDivWrapper onClick={(e) => handleAction(e, 'zoomIn')}>
                <span
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    userSelect: 'none',
                  }}
                >
                  {' '}
                  <ZoomInIcon size={18} /> Zoom In
                </span>
              </StylesDivWrapper>
            </OverlayTrigger>
            <OverlayTrigger
              overlay={
                <StyledTooltip>
                  {
                    <>
                      <span
                        style={{
                          fontSize: '12px',
                        }}
                      >
                        Ctrl
                      </span>{' '}
                      <span>+</span>{' '}
                      <span
                        style={{
                          fontSize: '12px',
                        }}
                      >
                        Minus
                      </span>
                    </>
                  }
                </StyledTooltip>
              }
            >
              <StylesDivWrapper onClick={(e) => handleAction(e, 'zoomOut')}>
                <span
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    userSelect: 'none',
                  }}
                >
                  {' '}
                  <ZoomOutIcon size={18} /> Zoom Out
                </span>
              </StylesDivWrapper>
            </OverlayTrigger>
            <OverlayTrigger overlay={<StyledTooltip>Left Arrow</StyledTooltip>}>
              <StylesDivWrapper
                onClick={(e) => handleAction(e, 'moveLeft')}
                className="move-left-btn"
              >
                <span
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    userSelect: 'none',
                  }}
                >
                  {' '}
                  <DropDownIcon size={18} rotate="left" /> Move Left
                </span>
              </StylesDivWrapper>
            </OverlayTrigger>
            <OverlayTrigger overlay={<StyledTooltip> Up Arrow</StyledTooltip>}>
              <StylesDivWrapper
                onClick={(e) => handleAction(e, 'moveUp')}
                className="move-top-btn"
              >
                <span
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    userSelect: 'none',
                  }}
                >
                  {' '}
                  <DropDownIcon size={18} rotate="up" /> Move Up
                </span>
              </StylesDivWrapper>
            </OverlayTrigger>
            <OverlayTrigger
              overlay={<StyledTooltip>Right Arrow</StyledTooltip>}
            >
              <StylesDivWrapper
                onClick={(e) => handleAction(e, 'moveRight')}
                className="move-right-btn"
              >
                <span
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    userSelect: 'none',
                  }}
                >
                  {' '}
                  <DropDownIcon size={18} rotate="right" /> Move Right
                </span>
              </StylesDivWrapper>
            </OverlayTrigger>
            <OverlayTrigger overlay={<StyledTooltip>Down Arrow</StyledTooltip>}>
              <StylesDivWrapper
                onClick={(e) => handleAction(e, 'moveDown')}
                className="move-bottom-btn"
              >
                <span
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    userSelect: 'none',
                  }}
                >
                  {' '}
                  <DropDownIcon size={18} rotate="down" /> Move Down
                </span>
              </StylesDivWrapper>
            </OverlayTrigger>
            <OverlayTrigger
              overlay={
                <StyledTooltip>
                  {
                    <>
                      <span
                        style={{
                          fontSize: '12px',
                        }}
                      >
                        Ctrl
                      </span>
                      <span>+</span>
                      <span
                        style={{
                          fontSize: '12px',
                        }}
                      >
                        Zero
                      </span>
                    </>
                  }
                </StyledTooltip>
              }
            >
              <StylesDivWrapper
                onClick={() => setIsReset(true)}
                style={{
                  backgroundColor: 'rgb(255 236 236)',
                  marginLeft: '10px',
                }}
              >
                <span
                  style={{
                    display: 'flex',
                    minWidth: '50px',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    userSelect: 'none',
                  }}
                >
                  <UndoIcon size={18} fill="#e96969" />
                  <span style={{ color: '#e96969' }}> Reset</span>
                </span>
              </StylesDivWrapper>
            </OverlayTrigger>
          </div>
        )}
        <div className="d-flex gap-2">
          <ShopStyled.EditButton
            color={'heading'}
            variant={'outline'}
            type="button"
            style={{
              boxShadow: 'none',
              backgroundColor: isProcessing && '#ccc',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
            }}
            onClick={() =>
              isProcessing ? null : (
                <>
                  {setSelectedImage(null)}{' '}
                  {dispatch(resetIsShowProcessedImageBasedOnId(image?.id))}
                </>
              )
            }
          >
            <ShopStyled.ButtonText>Cancel</ShopStyled.ButtonText>
          </ShopStyled.EditButton>

          {selectedImage && !image?.processed && (
            <ShopStyled.EditButton
              color={'primary'}
              type="button"
              disabled={isProcessing}
              onClick={handleSaveImage}
              style={{
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                filter: isProcessing && 'grayscale(100%)',
              }}
            >
              <ShopStyled.ButtonText className={'d-flex align-items-center'}>
                {'Save as Copy'}
              </ShopStyled.ButtonText>
            </ShopStyled.EditButton>
          )}

          {isRemovebackground && (
            <Dropdown as={ButtonGroup}>
              <BootstrapEditButton
                color={'primary'}
                type="button"
                disabled={isProcessing}
                onClick={() => (
                  <>
                    {setIsBackgroundImageShow(false)}
                    {setSelectedImage(null)}
                    {handleBackgroundRemoval(image, model)}
                  </>
                )}
                style={{
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  filter: isProcessing && 'grayscale(100%)',
                }}
              >
                Remove Background ({model})
              </BootstrapEditButton>

              <BootstrapDropDownButton
                split
                style={{
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  filter: isProcessing && 'grayscale(100%)',
                }}
                color={'primary'}
                type="button"
                disabled={isProcessing}
              />

              <Dropdown.Menu>
                <BootstrapDropDownItem
                  onClick={() => setModel('High')}
                  color={'primary'}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px',
                    }}
                  >
                    <span style={{ fontSize: '15px' }}>High</span>
                    <span>
                      High-quality processing ensures accuracy and reliability,
                      but expect a longer wait time.
                    </span>
                  </div>
                </BootstrapDropDownItem>
                <BootstrapDropDownItem
                  onClick={() => setModel('Medium')}
                  color={'primary'}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '5px',
                    }}
                  >
                    <span style={{ fontSize: '15px' }}>Medium</span>
                    <span>
                      Fast processing with low quality means quicker results,
                      but accuracy and reliability might be compromised.
                    </span>
                  </div>
                </BootstrapDropDownItem>
              </Dropdown.Menu>
            </Dropdown>
          )}

          {(!selectedImage || image?.processed) && !isRemovebackground && (
            <ShopStyled.EditButton
              color={'primary'}
              type="button"
              loading={isProcessing}
              onClick={() =>
                image?.processed && selectedImage ? (
                  handleSaveImage()
                ) : image?.processed ? (
                  handleSave()
                ) : isProcessing ? null : (
                  <>
                    {setIsBackgroundImageShow(false)}
                    {setSelectedImage(null)}
                    {handleBackgroundRemoval(image)}
                  </>
                )
              }
              style={{
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                filter: isProcessing && 'grayscale(100%)',
              }}
            >
              <ShopStyled.ButtonText className={'d-flex align-items-center'}>
                {image?.processed && selectedImage
                  ? ' Save as Copy'
                  : image?.processed
                  ? ' Save as Copy'
                  : `Remove Background(${model})`}
              </ShopStyled.ButtonText>
            </ShopStyled.EditButton>
          )}
        </div>
      </div>
      <DeleteConfirmation
        show={isReset}
        deleteButtonText={'Reset'}
        bodyText={
          'Are you sure you want to reset all the changes? this can not to be undone.'
        }
        onDelete={(e) => {
          handleAction(e, 'reset');
          setIsReset(false);
        }}
        onCancel={() => setIsReset(false)}
      />
    </StyledContainer>
  );
};
